import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    LinkIcon,
    ArrowRightIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ShieldExclamationIcon,
    ClockIcon,
    SignalIcon,
} from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import type { View, SystemView } from "./types";
import { WORLD_MAP_PATH, WORLD_MAP_VIEWBOX } from "./worldMapPaths";
import { VIEW_LABELS, VIEW_COLORS } from "./types";

/* ═══════════════════════════════════════════════════════════════════════════
   EXISTING COMPONENTS — unchanged
   ═══════════════════════════════════════════════════════════════════════════ */

export function StatCard({
    label,
    value,
    trend,
    trendDown,
}: {
    label: string;
    value: string;
    trend?: string;
    trendDown?: boolean;
}) {
    return (
        <div className="group relative overflow-hidden rounded-2xl border border-zinc-200/40 bg-white/60 p-6 shadow-lg shadow-blue-500/[0.03] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/[0.08] dark:border-zinc-700/40 dark:bg-zinc-800/60">
            <div className="absolute inset-y-0 left-0 w-1 rounded-l-2xl bg-gradient-to-b from-blue-400 via-blue-500 to-purple-500" />
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/5 to-purple-500/5 blur-2xl transition-all duration-500 group-hover:from-blue-500/10 group-hover:to-purple-500/10" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-400 dark:text-zinc-500">
                {label}
            </p>
            <p className="mt-2 bg-gradient-to-r from-zinc-900 to-zinc-700 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent dark:from-white dark:to-zinc-300">
                {value}
            </p>
            {trend && (
                <div className="mt-3 flex items-center gap-1.5">
                    {trendDown ? (
                        <ArrowTrendingDownIcon className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                        <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-blue-500" />
                    )}
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{trend}</span>
                </div>
            )}
        </div>
    );
}

export function HBar({ items }: { items: { label: string; value: number; max: number; color: string }[] }) {
    return (
        <div className="space-y-4">
            {items.map((item) => (
                <div key={item.label}>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-zinc-700 dark:text-zinc-300">{item.label}</span>
                        <span className="font-mono text-zinc-500 dark:text-zinc-400">{item.value}</span>
                    </div>
                    <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                        <div
                            className={`h-2.5 rounded-full ${item.color} transition-all duration-700 ease-out`}
                            style={{
                                width: `${Math.min((item.value / item.max) * 100, 100)}%`,
                                boxShadow: "0 0 8px rgba(59,130,246,0.15)",
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function RingChart({
    segments,
    size = 130,
    label,
}: {
    segments: { value: number; color: string; label: string }[];
    size?: number;
    label?: string;
}) {
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    let cumulative = 0;
    const gradientStops = segments.flatMap((seg) => {
        const start = (cumulative / total) * 360;
        cumulative += seg.value;
        const end = (cumulative / total) * 360;
        return [`${seg.color} ${start}deg ${end}deg`];
    });
    return (
        <div className="flex flex-col items-center gap-4">
            <div
                className="relative rounded-full shadow-lg shadow-blue-500/10"
                style={{ width: size, height: size, background: `conic-gradient(${gradientStops.join(", ")})` }}
            >
                <div
                    className="absolute inset-0 m-auto rounded-full bg-white/90 backdrop-blur-sm dark:bg-zinc-900/90"
                    style={{ width: size * 0.58, height: size * 0.58 }}
                />
                {label && (
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-extrabold text-zinc-700 dark:text-zinc-300">
                        {label}
                    </span>
                )}
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5">
                {segments.map((seg) => (
                    <div key={seg.label} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                        <span
                            className="inline-block h-2.5 w-2.5 rounded-full shadow-sm"
                            style={{ backgroundColor: seg.color }}
                        />
                        {seg.label} ({seg.value})
                    </div>
                ))}
            </div>
        </div>
    );
}

export function RingChartNext({
    segments,
    size = 180, // Increased default size to match the image scale better
    label,
}: {
    segments: { value: number; color: string; label: string }[];
    size?: number;
    label?: string;
}) {
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    let cumulative = 0;

    const gradientStops = segments.flatMap((seg) => {
        const start = (cumulative / total) * 360;
        cumulative += seg.value;
        const end = (cumulative / total) * 360;
        return [`${seg.color} ${start}deg ${end}deg`];
    });

    return (
        <div className="flex flex-row items-center gap-12">
            {/* The Chart */}
            <div
                className="relative shrink-0 rounded-full shadow-lg shadow-blue-500/5"
                style={{
                    width: size,
                    height: size,
                    background: `conic-gradient(${gradientStops.join(", ")})`,
                }}
            >
                <div
                    className="absolute inset-0 m-auto rounded-full bg-white/95 backdrop-blur-sm dark:bg-zinc-900/95"
                    style={{ width: size * 0.72, height: size * 0.72 }}
                />
                {label && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                        {/* Optional: Add the skull icon here if you have it, or just the text */}
                        <span className="text-xs font-bold uppercase tracking-tight text-zinc-800 dark:text-zinc-200">
                            {label}
                        </span>
                    </div>
                )}
            </div>

            {/* The Legend (Centered to the right) */}
            <div className="flex flex-col gap-y-3">
                {segments.map((seg) => {
                    const percentage = Math.round((seg.value / total) * 100);
                    return (
                        <div key={seg.label} className="flex items-center gap-4">
                            {/* Color Square */}
                            <div className="h-4 w-4 shrink-0 rounded-[4px]" style={{ backgroundColor: seg.color }} />

                            {/* Percentage */}
                            <span className="w-8 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                                {percentage}%
                            </span>

                            {/* Label and Count */}
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                    {seg.label}
                                </span>
                                <span className="text-xs text-zinc-400 dark:text-zinc-500">({seg.value})</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function Sparkline({ data, color = "bg-blue-400 dark:bg-blue-500" }: { data: number[]; color?: string }) {
    const max = Math.max(...data);
    return (
        <div className="flex h-14 items-end gap-1.5">
            {data.map((v, i) => (
                <div
                    key={i}
                    className={`flex-1 rounded-md ${color} opacity-60 shadow-sm transition-all duration-300 hover:opacity-100 hover:shadow-md hover:shadow-blue-500/20`}
                    style={{ height: `${Math.max((v / max) * 100, 4)}%` }}
                    title={String(v)}
                />
            ))}
        </div>
    );
}

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <div
            className={`relative overflow-hidden rounded-2xl border border-zinc-200/50 bg-white/70 p-6 shadow-lg shadow-blue-500/[0.03] backdrop-blur-xl transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/[0.06] dark:border-zinc-700/40 dark:bg-zinc-800/70 ${className}`}
        >
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/[0.07] via-transparent to-purple-500/[0.07]" />
            <div className="relative">{children}</div>
        </div>
    );
}

export function SectionTitle({ title, description }: { title: string; description?: string }) {
    return (
        <div className="mb-8 mt-14 first:mt-0">
            <h2 className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-600 bg-clip-text text-xl font-bold tracking-tight text-transparent dark:from-white dark:via-zinc-200 dark:to-zinc-400">
                {title}
            </h2>
            {description && (
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">{description}</p>
            )}
            <div className="mt-4 h-px bg-gradient-to-r from-blue-500/50 via-purple-500/20 to-transparent" />
        </div>
    );
}

export function FeedEntry({
    time,
    icon: Icon,
    color,
    children,
    linkedSystem,
    onNavigate,
}: {
    time: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    color: string;
    children: React.ReactNode;
    linkedSystem?: SystemView;
    onNavigate?: (view: View) => void;
}) {
    return (
        <div className="group flex gap-3 rounded-xl px-2 py-3 transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent dark:hover:from-blue-950/20 dark:hover:to-transparent">
            <div
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${color} shadow-sm backdrop-blur-sm`}
            >
                <Icon className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-700 dark:text-zinc-300">{children}</p>
                <div className="mt-1 flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-400">{time}</span>
                    {linkedSystem && onNavigate && (
                        <button
                            onClick={() => onNavigate(linkedSystem)}
                            className="flex items-center gap-0.5 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            <LinkIcon className="h-3 w-3" />
                            {VIEW_LABELS[linkedSystem]}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function CorrelationBadges({ systems, onNavigate }: { systems: SystemView[]; onNavigate: (v: View) => void }) {
    return (
        <div className="flex flex-wrap gap-1">
            {systems.map((sys) => (
                <button key={sys} onClick={() => onNavigate(sys)} className="inline-flex items-center gap-0.5">
                    <Badge color={VIEW_COLORS[sys]}>
                        <LinkIcon className="mr-0.5 inline h-2.5 w-2.5" />
                        {VIEW_LABELS[sys]}
                    </Badge>
                </button>
            ))}
        </div>
    );
}

export function IncidentChain({
    steps,
    onNavigate,
}: {
    steps: { system: SystemView; label: string }[];
    onNavigate: (v: View) => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {steps.map((step, i) => (
                <div key={i} className="flex items-center gap-1.5">
                    <button
                        onClick={() => onNavigate(step.system)}
                        className="flex items-center gap-1 rounded-xl border border-zinc-200/50 bg-white/70 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm backdrop-blur-sm transition-all duration-200 hover:border-blue-400/50 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/50 dark:bg-zinc-800/70 dark:text-zinc-300 dark:hover:border-blue-500/50"
                    >
                        <Badge color={VIEW_COLORS[step.system]} className="mr-1">
                            {VIEW_LABELS[step.system]}
                        </Badge>
                        {step.label}
                    </button>
                    {i < steps.length - 1 && <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-blue-400/40" />}
                </div>
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   NEW OVERENGINEERED COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Shared keyframe styles injected once ─────────────────────────────── */

const INJECTED_STYLES_ID = "shared-overengineered-keyframes";

function useInjectKeyframes() {
    useEffect(() => {
        if (typeof document === "undefined") return;
        if (document.getElementById(INJECTED_STYLES_ID)) return;
        const style = document.createElement("style");
        style.id = INJECTED_STYLES_ID;
        style.textContent = `
@keyframes oe-pulse-ring { 0%,100%{opacity:.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.08)} }
@keyframes oe-spin-slow { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
@keyframes oe-float { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-6px)} }
@keyframes oe-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.85)} }
@keyframes oe-blink-live { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes oe-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
@keyframes oe-glow-rotate { 0%{--angle:0deg} 100%{--angle:360deg} }
@keyframes oe-particle-drift {
  0%{transform:translate(0,0) scale(1);opacity:.6}
  25%{opacity:.9}
  50%{transform:translate(var(--dx),var(--dy)) scale(1.2);opacity:.5}
  75%{opacity:.8}
  100%{transform:translate(0,0) scale(1);opacity:.6}
}
@keyframes oe-gauge-needle { 0%{transform:rotate(-90deg)} }
@keyframes oe-radar-draw { 0%{opacity:0;transform:scale(.3)} 100%{opacity:.55;transform:scale(1)} }
@keyframes oe-area-reveal { 0%{clip-path:inset(0 100% 0 0)} 100%{clip-path:inset(0 0 0 0)} }
@keyframes oe-counter-fade { 0%{opacity:0;transform:translateY(8px)} 100%{opacity:1;transform:translateY(0)} }
@keyframes oe-tab-indicator { 0%{transform:scaleX(.7);opacity:.5} 100%{transform:scaleX(1);opacity:1} }
@keyframes oe-map-pulse { 0%,100%{r:3;opacity:.9} 50%{r:7;opacity:.3} }
@keyframes oe-progress-fill { 0%{stroke-dashoffset:var(--circumference)} }
@keyframes oe-heat-pop { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.15);opacity:1} 100%{transform:scale(1);opacity:1} }
@keyframes oe-skeleton-pulse { 0%{opacity:.6} 50%{opacity:.3} 100%{opacity:.6} }
@keyframes oe-glow-border {
  0%{background-position:0% 50%}
  50%{background-position:100% 50%}
  100%{background-position:0% 50%}
}
        `;
        document.head.appendChild(style);
    }, []);
}

/* ── 1. AnimatedCounter ───────────────────────────────────────────────── */

function easeOutExpo(t: number): number {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

export function AnimatedCounter({
    target,
    duration = 2000,
    prefix = "",
    suffix = "",
    decimals = 0,
    className = "",
}: {
    target: number;
    duration?: number;
    prefix?: string;
    suffix?: string;
    decimals?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const [display, setDisplay] = useState("0");
    const rafRef = useRef<number>(0);
    const startTimeRef = useRef<number>(0);
    const hasStarted = useRef(false);
    const elRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !hasStarted.current) {
                    hasStarted.current = true;
                    startTimeRef.current = performance.now();
                    const tick = (now: number) => {
                        const elapsed = now - startTimeRef.current;
                        const progress = Math.min(elapsed / duration, 1);
                        const easedProgress = easeOutExpo(progress);
                        const current = easedProgress * target;
                        setDisplay(current.toFixed(decimals));
                        if (progress < 1) {
                            rafRef.current = requestAnimationFrame(tick);
                        }
                    };
                    rafRef.current = requestAnimationFrame(tick);
                }
            },
            { threshold: 0.2 },
        );
        if (elRef.current) observer.observe(elRef.current);
        return () => {
            observer.disconnect();
            cancelAnimationFrame(rafRef.current);
        };
    }, [target, duration, decimals]);

    return (
        <span
            ref={elRef}
            className={`inline-block bg-gradient-to-r from-zinc-900 to-zinc-600 bg-clip-text font-extrabold tabular-nums text-transparent dark:from-white dark:to-zinc-400 ${className}`}
            style={{ animation: "oe-counter-fade 0.6s ease-out both" }}
        >
            {prefix}
            {display}
            {suffix}
        </span>
    );
}

/* ── 2. RadarChart ────────────────────────────────────────────────────── */

export function RadarChart({
    axes,
    size = 200,
    color = "#3b82f6",
    fillOpacity = 0.25,
    className = "",
}: {
    axes: { label: string; value: number; max?: number }[];
    size?: number;
    color?: string;
    fillOpacity?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const cx = size / 2;
    const cy = size / 2;
    const r = size * 0.38;
    const n = axes.length;
    const rings = [0.25, 0.5, 0.75, 1];

    const pointAt = useCallback(
        (index: number, radius: number) => {
            const angle = (Math.PI * 2 * index) / n - Math.PI / 2;
            return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
        },
        [cx, cy, n],
    );

    const polygonPoints = useMemo(() => {
        return axes
            .map((a, i) => {
                const max = a.max ?? 100;
                const frac = Math.min(a.value / max, 1);
                const p = pointAt(i, frac * r);
                return `${p.x},${p.y}`;
            })
            .join(" ");
    }, [axes, pointAt, r]);

    return (
        <div className={`inline-flex flex-col items-center ${className}`}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="drop-shadow-lg">
                {/* Grid rings */}
                {rings.map((frac) => (
                    <polygon
                        key={frac}
                        points={Array.from({ length: n }, (_, i) => pointAt(i, frac * r))
                            .map((p) => `${p.x},${p.y}`)
                            .join(" ")}
                        fill="none"
                        stroke="currentColor"
                        className="text-zinc-200 dark:text-zinc-700"
                        strokeWidth={0.5}
                    />
                ))}
                {/* Axis lines */}
                {axes.map((_, i) => {
                    const p = pointAt(i, r);
                    return (
                        <line
                            key={i}
                            x1={cx}
                            y1={cy}
                            x2={p.x}
                            y2={p.y}
                            stroke="currentColor"
                            className="text-zinc-200 dark:text-zinc-700"
                            strokeWidth={0.5}
                        />
                    );
                })}
                {/* Data polygon */}
                <polygon
                    points={polygonPoints}
                    fill={color}
                    fillOpacity={fillOpacity}
                    stroke={color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    style={{ animation: "oe-radar-draw 0.8s ease-out both", transformOrigin: `${cx}px ${cy}px` }}
                />
                {/* Data points */}
                {axes.map((a, i) => {
                    const max = a.max ?? 100;
                    const frac = Math.min(a.value / max, 1);
                    const p = pointAt(i, frac * r);
                    return <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} className="drop-shadow-sm" />;
                })}
                {/* Labels */}
                {axes.map((a, i) => {
                    const p = pointAt(i, r + 16);
                    return (
                        <text
                            key={i}
                            x={p.x}
                            y={p.y}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className="fill-zinc-500 text-[10px] font-medium dark:fill-zinc-400"
                        >
                            {a.label}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
}

/* ── 3. GeoThreatMap ──────────────────────────────────────────────────── */

export function GeoThreatMap({
    threats,
    width = 500,
    height = 200,
    className = "",
}: {
    threats: { x: number; y: number; label?: string; severity?: "low" | "medium" | "high" | "critical" }[];
    width?: number;
    height?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const severityColor: Record<string, string> = {
        low: "#22c55e",
        medium: "#f59e0b",
        high: "#f97316",
        critical: "#ef4444",
    };

    return (
        <div
            className={`relative overflow-hidden rounded-2xl border border-zinc-200/30 bg-gradient-to-br from-zinc-50 via-blue-50/30 to-zinc-50 p-4 shadow-inner dark:border-zinc-700/30 dark:from-zinc-900 dark:via-blue-950/20 dark:to-zinc-900 ${className}`}
        >
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="opacity-80">
                {/* World outline */}
                <g transform={`scale(${width / WORLD_MAP_VIEWBOX.width}, ${height / WORLD_MAP_VIEWBOX.height})`}>
                    <path
                        d={WORLD_MAP_PATH}
                        fill="none"
                        stroke="currentColor"
                        className="text-zinc-300 dark:text-zinc-600"
                        strokeWidth={1.5 / (width / WORLD_MAP_VIEWBOX.width)}
                        strokeLinejoin="round"
                    />
                </g>
                {/* Grid lines */}
                {Array.from({ length: 5 }, (_, i) => (
                    <line
                        key={`h${i}`}
                        x1={0}
                        y1={(i * height) / 4}
                        x2={width}
                        y2={(i * height) / 4}
                        stroke="currentColor"
                        className="text-zinc-100 dark:text-zinc-800"
                        strokeWidth={0.5}
                        strokeDasharray="4 4"
                    />
                ))}
                {Array.from({ length: 7 }, (_, i) => (
                    <line
                        key={`v${i}`}
                        x1={(i * width) / 6}
                        y1={0}
                        x2={(i * width) / 6}
                        y2={height}
                        stroke="currentColor"
                        className="text-zinc-100 dark:text-zinc-800"
                        strokeWidth={0.5}
                        strokeDasharray="4 4"
                    />
                ))}
                {/* Threat dots */}
                {threats.map((t, i) => {
                    const c = severityColor[t.severity ?? "high"];
                    return (
                        <g key={i}>
                            <circle cx={t.x} cy={t.y} r={5} fill={c} fillOpacity={0.15}>
                                <animate
                                    attributeName="r"
                                    values="5;14;5"
                                    dur="2.5s"
                                    repeatCount="indefinite"
                                    begin={`${i * 0.4}s`}
                                />
                                <animate
                                    attributeName="opacity"
                                    values="0.4;0.05;0.4"
                                    dur="2.5s"
                                    repeatCount="indefinite"
                                    begin={`${i * 0.4}s`}
                                />
                            </circle>
                            <circle cx={t.x} cy={t.y} r={3} fill={c} className="drop-shadow-md">
                                <animate
                                    attributeName="r"
                                    values="3;4;3"
                                    dur="1.5s"
                                    repeatCount="indefinite"
                                    begin={`${i * 0.3}s`}
                                />
                            </circle>
                            {t.label && (
                                <text
                                    x={t.x + 8}
                                    y={t.y + 3}
                                    className="fill-zinc-500 text-[8px] font-medium dark:fill-zinc-400"
                                >
                                    {t.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

/* ── 4. ProgressRing ──────────────────────────────────────────────────── */

export function ProgressRing({
    value,
    max = 100,
    size = 80,
    strokeWidth = 6,
    color = "#3b82f6",
    showLabel = true,
    className = "",
}: {
    value: number;
    max?: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
    showLabel?: boolean;
    className?: string;
}) {
    useInjectKeyframes();
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const pct = Math.min(value / max, 1);
    const offset = circumference * (1 - pct);

    return (
        <div
            className={`relative inline-flex items-center justify-center ${className}`}
            style={{ width: size, height: size }}
        >
            <svg width={size} height={size} className="-rotate-90">
                {/* Background track */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    className="text-zinc-200 dark:text-zinc-700"
                    strokeWidth={strokeWidth}
                />
                {/* Filled arc */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={
                        {
                            "--circumference": `${circumference}`,
                            animation: "oe-progress-fill 1.2s ease-out both",
                            filter: `drop-shadow(0 0 4px ${color}40)`,
                        } as React.CSSProperties
                    }
                />
            </svg>
            {showLabel && (
                <span className="absolute text-sm font-bold text-zinc-700 dark:text-zinc-300">
                    {Math.round(pct * 100)}%
                </span>
            )}
        </div>
    );
}

/* ── 5. StatusPulse ───────────────────────────────────────────────────── */

export function StatusPulse({
    status,
    label,
    className = "",
}: {
    status: "green" | "amber" | "red";
    label?: string;
    className?: string;
}) {
    useInjectKeyframes();
    const colorMap = {
        green: { bg: "bg-emerald-500", ring: "ring-emerald-400/30", glow: "shadow-emerald-500/40" },
        amber: { bg: "bg-amber-500", ring: "ring-amber-400/30", glow: "shadow-amber-500/40" },
        red: { bg: "bg-red-500", ring: "ring-red-400/30", glow: "shadow-red-500/40" },
    };
    const c = colorMap[status];

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <span className="relative flex h-3 w-3">
                <span
                    className={`absolute inline-flex h-full w-full rounded-full ${c.bg} opacity-75`}
                    style={{ animation: "oe-pulse-dot 1.5s ease-in-out infinite" }}
                />
                <span
                    className={`relative inline-flex h-3 w-3 rounded-full ${c.bg} shadow-lg ${c.glow} ring-2 ${c.ring}`}
                />
            </span>
            {label && (
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                    {label}
                </span>
            )}
        </div>
    );
}

/* ── 6. Gauge ─────────────────────────────────────────────────────────── */

export function Gauge({
    value,
    min = 0,
    max = 100,
    label,
    size = 160,
    className = "",
}: {
    value: number;
    min?: number;
    max?: number;
    label?: string;
    size?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const pct = Math.min(Math.max((value - min) / (max - min), 0), 1);
    const needleAngle = -90 + pct * 180;
    const cx = size / 2;
    const cy = size * 0.6;
    const outerR = size * 0.42;
    const innerR = size * 0.32;

    const arcPath = (startDeg: number, endDeg: number, radius: number) => {
        const s = ((startDeg - 180) * Math.PI) / 180;
        const e = ((endDeg - 180) * Math.PI) / 180;
        const x1 = cx + Math.cos(s) * radius;
        const y1 = cy + Math.sin(s) * radius;
        const x2 = cx + Math.cos(e) * radius;
        const y2 = cy + Math.sin(e) * radius;
        const large = endDeg - startDeg > 180 ? 1 : 0;
        return `M${x1},${y1} A${radius},${radius} 0 ${large} 1 ${x2},${y2}`;
    };

    const zones = [
        { from: 0, to: 60, color: "#22c55e" },
        { from: 60, to: 120, color: "#f59e0b" },
        { from: 120, to: 180, color: "#ef4444" },
    ];

    return (
        <div className={`inline-flex flex-col items-center ${className}`}>
            <svg width={size} height={size * 0.7} viewBox={`0 0 ${size} ${size * 0.7}`}>
                {/* Colored arcs */}
                {zones.map((z, i) => (
                    <path
                        key={i}
                        d={arcPath(z.from, z.to, outerR)}
                        fill="none"
                        stroke={z.color}
                        strokeWidth={outerR - innerR}
                        strokeLinecap="butt"
                        opacity={0.25}
                    />
                ))}
                {/* Active arc overlay */}
                <path
                    d={arcPath(0, pct * 180, outerR)}
                    fill="none"
                    stroke={pct < 0.33 ? "#22c55e" : pct < 0.66 ? "#f59e0b" : "#ef4444"}
                    strokeWidth={outerR - innerR}
                    strokeLinecap="round"
                    className="drop-shadow-md"
                />
                {/* Needle */}
                <g
                    style={{
                        transformOrigin: `${cx}px ${cy}px`,
                        transform: `rotate(${needleAngle}deg)`,
                        animation: "oe-gauge-needle 1s ease-out both",
                    }}
                >
                    <line
                        x1={cx}
                        y1={cy}
                        x2={cx + outerR - 4}
                        y2={cy}
                        stroke="currentColor"
                        className="text-zinc-700 dark:text-zinc-200"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                    />
                </g>
                {/* Center dot */}
                <circle cx={cx} cy={cy} r={5} className="fill-zinc-600 dark:fill-zinc-300" />
                {/* Tick labels */}
                <text
                    x={cx - outerR - 4}
                    y={cy + 14}
                    className="fill-zinc-400 text-[9px] dark:fill-zinc-500"
                    textAnchor="end"
                >
                    {min}
                </text>
                <text
                    x={cx + outerR + 4}
                    y={cy + 14}
                    className="fill-zinc-400 text-[9px] dark:fill-zinc-500"
                    textAnchor="start"
                >
                    {max}
                </text>
            </svg>
            <div className="-mt-1 flex flex-col items-center">
                <span className="text-2xl font-extrabold text-zinc-800 dark:text-zinc-100">{value}</span>
                {label && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                        {label}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ── 7. AreaChart ─────────────────────────────────────────────────────── */

export function AreaChart({
    data,
    width = 400,
    height = 160,
    color = "#3b82f6",
    showGrid = true,
    labels,
    className = "",
}: {
    data: number[];
    width?: number;
    height?: number;
    color?: string;
    showGrid?: boolean;
    labels?: string[];
    className?: string;
}) {
    useInjectKeyframes();
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const padding = { top: 10, right: 10, bottom: 24, left: 10 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;
    const maxVal = Math.max(...data, 1);
    const minVal = Math.min(...data, 0);
    const range = maxVal - minVal || 1;

    const points = useMemo(
        () =>
            data.map((v, i) => ({
                x: padding.left + (i / Math.max(data.length - 1, 1)) * chartW,
                y: padding.top + chartH - ((v - minVal) / range) * chartH,
            })),
        [data, chartW, chartH, padding.left, padding.top, minVal, range],
    );

    const linePath = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
    const areaPath =
        linePath +
        ` L${points[points.length - 1]?.x ?? 0},${padding.top + chartH} L${padding.left},${padding.top + chartH} Z`;
    const gradId = useMemo(() => `area-grad-${Math.random().toString(36).slice(2, 8)}`, []);

    return (
        <div className={`relative ${className}`}>
            <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${width} ${height}`}
                className="overflow-visible"
                onMouseLeave={() => setHoverIdx(null)}
            >
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                {/* Grid */}
                {showGrid &&
                    Array.from({ length: 5 }, (_, i) => {
                        const y = padding.top + (i * chartH) / 4;
                        return (
                            <line
                                key={i}
                                x1={padding.left}
                                y1={y}
                                x2={padding.left + chartW}
                                y2={y}
                                stroke="currentColor"
                                className="text-zinc-100 dark:text-zinc-800"
                                strokeWidth={0.5}
                                strokeDasharray="3 3"
                            />
                        );
                    })}
                {/* Area fill */}
                <path d={areaPath} fill={`url(#${gradId})`} style={{ animation: "oe-area-reveal 1s ease-out both" }} />
                {/* Line */}
                <path
                    d={linePath}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    style={{
                        animation: "oe-area-reveal 1s ease-out both",
                        filter: `drop-shadow(0 1px 3px ${color}40)`,
                    }}
                />
                {/* Data points */}
                {points.map((p, i) => (
                    <circle
                        key={i}
                        cx={p.x}
                        cy={p.y}
                        r={hoverIdx === i ? 5 : 2.5}
                        fill={color}
                        stroke="white"
                        strokeWidth={hoverIdx === i ? 2 : 0}
                        className="cursor-pointer transition-all duration-150"
                        onMouseEnter={() => setHoverIdx(i)}
                    />
                ))}
                {/* Hover tooltip */}
                {hoverIdx !== null && points[hoverIdx] && (
                    <g>
                        <line
                            x1={points[hoverIdx].x}
                            y1={padding.top}
                            x2={points[hoverIdx].x}
                            y2={padding.top + chartH}
                            stroke={color}
                            strokeWidth={0.5}
                            strokeDasharray="3 3"
                            opacity={0.5}
                        />
                        <rect
                            x={points[hoverIdx].x - 24}
                            y={points[hoverIdx].y - 24}
                            width={48}
                            height={18}
                            rx={6}
                            className="fill-zinc-800 dark:fill-zinc-100"
                            opacity={0.9}
                        />
                        <text
                            x={points[hoverIdx].x}
                            y={points[hoverIdx].y - 12}
                            textAnchor="middle"
                            className="fill-white text-[10px] font-bold dark:fill-zinc-900"
                        >
                            {data[hoverIdx]}
                        </text>
                    </g>
                )}
                {/* X-axis labels */}
                {labels &&
                    labels.map((l, i) => {
                        const x = padding.left + (i / Math.max(labels.length - 1, 1)) * chartW;
                        return (
                            <text
                                key={i}
                                x={x}
                                y={height - 4}
                                textAnchor="middle"
                                className="fill-zinc-400 text-[9px] dark:fill-zinc-500"
                            >
                                {l}
                            </text>
                        );
                    })}
            </svg>
        </div>
    );
}

/* ── 8. Treemap ───────────────────────────────────────────────────────── */

interface TreemapItem {
    label: string;
    value: number;
    color?: string;
}

function layoutTreemap(
    items: TreemapItem[],
    width: number,
    height: number,
): { item: TreemapItem; x: number; y: number; w: number; h: number }[] {
    const total = items.reduce((s, it) => s + it.value, 0);
    const sorted = [...items].sort((a, b) => b.value - a.value);
    const rects: { item: TreemapItem; x: number; y: number; w: number; h: number }[] = [];
    let x = 0,
        y = 0,
        remainW = width,
        remainH = height;
    let horizontal = width >= height;

    for (const item of sorted) {
        const frac = total > 0 ? item.value / total : 0;
        if (horizontal) {
            const w = frac * width;
            rects.push({ item, x, y, w, h: remainH });
            x += w;
            remainW -= w;
        } else {
            const h = frac * height;
            rects.push({ item, x, y, w: remainW, h });
            y += h;
            remainH -= h;
        }
        horizontal = !horizontal;
    }
    return rects;
}

const TREEMAP_COLORS = [
    "#3b82f6",
    "#8b5cf6",
    "#06b6d4",
    "#22c55e",
    "#f59e0b",
    "#ef4444",
    "#ec4899",
    "#14b8a6",
    "#6366f1",
    "#f97316",
];

export function Treemap({
    items,
    width = 400,
    height = 200,
    className = "",
}: {
    items: TreemapItem[];
    width?: number;
    height?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const rects = useMemo(() => layoutTreemap(items, width, height), [items, width, height]);

    return (
        <div
            className={`overflow-hidden rounded-xl border border-zinc-200/30 shadow-inner dark:border-zinc-700/30 ${className}`}
        >
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
                {rects.map((r, i) => {
                    const color = r.item.color ?? TREEMAP_COLORS[i % TREEMAP_COLORS.length];
                    return (
                        <g key={i} className="group cursor-pointer">
                            <rect
                                x={r.x + 1}
                                y={r.y + 1}
                                width={Math.max(r.w - 2, 0)}
                                height={Math.max(r.h - 2, 0)}
                                rx={4}
                                fill={color}
                                opacity={0.7}
                                className="hover:opacity-1 transition-opacity duration-200"
                            />
                            {r.w > 40 && r.h > 20 && (
                                <text
                                    x={r.x + r.w / 2}
                                    y={r.y + r.h / 2 - 4}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className="pointer-events-none fill-white text-[10px] font-bold drop-shadow-sm"
                                >
                                    {r.item.label}
                                </text>
                            )}
                            {r.w > 30 && r.h > 30 && (
                                <text
                                    x={r.x + r.w / 2}
                                    y={r.y + r.h / 2 + 10}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className="pointer-events-none fill-white/70 text-[9px] font-medium"
                                >
                                    {r.item.value}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

/* ── 9. ActivityTimeline ──────────────────────────────────────────────── */

interface TimelineEntry {
    time: string;
    title: string;
    description?: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    color?: string;
}

export function ActivityTimeline({ entries, className = "" }: { entries: TimelineEntry[]; className?: string }) {
    useInjectKeyframes();
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

    return (
        <div className={`relative ${className}`}>
            {/* Vertical line */}
            <div className="absolute bottom-2 left-[15px] top-2 w-px bg-gradient-to-b from-blue-400/40 via-purple-400/20 to-transparent" />
            <div className="space-y-1">
                {entries.map((entry, i) => {
                    const Icon = entry.icon ?? ClockIcon;
                    const isExpanded = expandedIdx === i;
                    return (
                        <div key={i} className="group relative flex gap-3 pl-0">
                            {/* Dot */}
                            <div
                                className={`relative z-10 mt-1 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full ${entry.color ?? "bg-blue-500/15"} shadow-sm ring-2 ring-white/80 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 dark:ring-zinc-900/80`}
                            >
                                <Icon className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
                            </div>
                            {/* Content */}
                            <div
                                className="flex-1 cursor-pointer rounded-xl px-3 py-2 transition-all duration-200 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/50"
                                onClick={() => setExpandedIdx(isExpanded ? null : i)}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                                        {entry.title}
                                    </span>
                                    <span className="font-mono text-[10px] text-zinc-400">{entry.time}</span>
                                    {entry.description && (
                                        <ChevronRightIcon
                                            className={`h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                                        />
                                    )}
                                </div>
                                {entry.description && isExpanded && (
                                    <p
                                        className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400"
                                        style={{ animation: "oe-counter-fade 0.3s ease-out both" }}
                                    >
                                        {entry.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ── 10. KanbanColumn ─────────────────────────────────────────────────── */

interface KanbanCard {
    id: string;
    title: string;
    tags?: { label: string; color?: string }[];
    assignee?: string;
    priority?: "low" | "medium" | "high" | "critical";
}

export function KanbanColumn({
    title,
    cards,
    accentColor = "#3b82f6",
    className = "",
}: {
    title: string;
    cards: KanbanCard[];
    accentColor?: string;
    className?: string;
}) {
    useInjectKeyframes();
    const priorityColors: Record<string, string> = {
        low: "bg-emerald-500",
        medium: "bg-amber-500",
        high: "bg-orange-500",
        critical: "bg-red-500",
    };

    return (
        <div
            className={`flex w-72 flex-col rounded-2xl border border-zinc-200/50 bg-white/50 shadow-lg shadow-zinc-500/[0.03] backdrop-blur-xl dark:border-zinc-700/40 dark:bg-zinc-800/50 ${className}`}
        >
            {/* Header */}
            <div className="flex items-center gap-2 border-b border-zinc-200/40 px-4 py-3 dark:border-zinc-700/30">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: accentColor }} />
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300">{title}</h3>
                <span className="ml-auto rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400">
                    {cards.length}
                </span>
            </div>
            {/* Cards */}
            <div className="flex flex-col gap-2 p-3">
                {cards.map((card) => (
                    <div
                        key={card.id}
                        className="group cursor-grab rounded-xl border border-zinc-200/40 bg-white/80 p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-blue-500/5 active:cursor-grabbing dark:border-zinc-700/30 dark:bg-zinc-800/80"
                    >
                        <div className="flex items-start gap-2">
                            {card.priority && (
                                <div
                                    className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${priorityColors[card.priority]}`}
                                />
                            )}
                            <p className="flex-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">{card.title}</p>
                        </div>
                        {card.tags && card.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {card.tags.map((tag, i) => (
                                    <span
                                        key={i}
                                        className="rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-blue-600 dark:bg-blue-500/15 dark:text-blue-400"
                                    >
                                        {tag.label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {card.assignee && (
                            <div className="mt-2 flex items-center gap-1.5">
                                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-[7px] font-bold text-white">
                                    {card.assignee.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-[10px] text-zinc-400">{card.assignee}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── 11. MiniSparkline ────────────────────────────────────────────────── */

export function MiniSparkline({
    data,
    width = 60,
    height = 20,
    color = "#3b82f6",
    className = "",
}: {
    data: number[];
    width?: number;
    height?: number;
    color?: string;
    className?: string;
}) {
    const maxVal = Math.max(...data, 1);
    const minVal = Math.min(...data, 0);
    const range = maxVal - minVal || 1;

    const points = data
        .map((v, i) => {
            const x = (i / Math.max(data.length - 1, 1)) * width;
            const y = height - ((v - minVal) / range) * height * 0.85 - height * 0.075;
            return `${x},${y}`;
        })
        .join(" ");

    const areaPoints = `0,${height} ${points} ${width},${height}`;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`inline-block ${className}`}>
            <polygon points={areaPoints} fill={color} fillOpacity={0.12} />
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {/* End dot */}
            {data.length > 0 && (
                <circle
                    cx={width}
                    cy={height - ((data[data.length - 1] - minVal) / range) * height * 0.85 - height * 0.075}
                    r={2}
                    fill={color}
                />
            )}
        </svg>
    );
}

/* ── 12. Breadcrumbs ──────────────────────────────────────────────────── */

export function Breadcrumbs({
    items,
    className = "",
}: {
    items: { label: string; onClick?: () => void }[];
    className?: string;
}) {
    return (
        <nav className={`flex items-center gap-1 ${className}`} aria-label="Breadcrumb">
            {items.map((item, i) => (
                <div key={i} className="flex items-center gap-1">
                    {i > 0 && <ChevronRightIcon className="h-3.5 w-3.5 text-zinc-300 dark:text-zinc-600" />}
                    {item.onClick ? (
                        <button
                            onClick={item.onClick}
                            className="rounded-md px-1.5 py-0.5 text-xs font-medium text-zinc-500 transition-all duration-150 hover:bg-zinc-100 hover:text-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                        >
                            {item.label}
                        </button>
                    ) : (
                        <span className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-zinc-800 dark:text-zinc-200">
                            {item.label}
                        </span>
                    )}
                </div>
            ))}
        </nav>
    );
}

/* ── 13. TabGroup ─────────────────────────────────────────────────────── */

export function TabGroup({
    tabs,
    activeTab,
    onChange,
    className = "",
}: {
    tabs: { key: string; label: string; icon?: React.ComponentType<React.SVGProps<SVGSVGElement>> }[];
    activeTab: string;
    onChange: (key: string) => void;
    className?: string;
}) {
    useInjectKeyframes();
    const containerRef = useRef<HTMLDivElement>(null);
    const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const activeEl = container.querySelector(`[data-tab-key="${activeTab}"]`) as HTMLElement | null;
        if (activeEl) {
            setIndicatorStyle({
                left: activeEl.offsetLeft,
                width: activeEl.offsetWidth,
                transition: "left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1)",
            });
        }
    }, [activeTab]);

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div className="flex gap-0.5 rounded-xl bg-zinc-100/80 p-1 backdrop-blur-sm dark:bg-zinc-800/80">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            data-tab-key={tab.key}
                            onClick={() => onChange(tab.key)}
                            className={`relative z-10 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                                isActive
                                    ? "text-zinc-900 dark:text-white"
                                    : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                            }`}
                        >
                            {Icon && <Icon className="h-3.5 w-3.5" />}
                            {tab.label}
                        </button>
                    );
                })}
            </div>
            {/* Sliding indicator */}
            <div
                className="absolute top-1 h-[calc(100%-8px)] rounded-lg bg-white shadow-sm dark:bg-zinc-700"
                style={{ ...indicatorStyle, animation: "oe-tab-indicator 0.2s ease-out" }}
            />
        </div>
    );
}

/* ── 14. MetricDelta ──────────────────────────────────────────────────── */

export function MetricDelta({
    label,
    current,
    delta,
    deltaLabel,
    format = (v) => String(v),
    className = "",
}: {
    label: string;
    current: number;
    delta: number;
    deltaLabel?: string;
    format?: (v: number) => string;
    className?: string;
}) {
    const isPositive = delta >= 0;
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                    {label}
                </p>
                <p className="mt-0.5 text-2xl font-extrabold text-zinc-800 dark:text-zinc-100">{format(current)}</p>
            </div>
            <div
                className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold ${
                    isPositive
                        ? "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
                        : "bg-red-500/10 text-red-600 dark:bg-red-500/15 dark:text-red-400"
                }`}
            >
                {isPositive ? (
                    <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
                ) : (
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5" />
                )}
                {isPositive ? "+" : ""}
                {format(delta)}
                {deltaLabel && <span className="ml-0.5 font-medium opacity-70">{deltaLabel}</span>}
            </div>
        </div>
    );
}

/* ── 15. HeatCalendar ─────────────────────────────────────────────────── */

export function HeatCalendar({
    data,
    weeks = 12,
    colorScale = ["#161b22", "#0e4429", "#006d32", "#26a641", "#39d353"],
    className = "",
}: {
    /** Array of values, one per day (most recent last). Missing days = 0. */
    data: number[];
    weeks?: number;
    colorScale?: string[];
    className?: string;
}) {
    useInjectKeyframes();
    const totalDays = weeks * 7;
    const padded = useMemo(() => {
        const arr = new Array(totalDays).fill(0);
        const offset = totalDays - data.length;
        data.forEach((v, i) => {
            if (i + offset >= 0) arr[i + offset] = v;
        });
        return arr;
    }, [data, totalDays]);

    const maxVal = Math.max(...padded, 1);
    const cellSize = 12;
    const gap = 2;
    const svgW = weeks * (cellSize + gap);
    const svgH = 7 * (cellSize + gap);

    return (
        <div className={`inline-block ${className}`}>
            <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
                {padded.map((val, i) => {
                    const week = Math.floor(i / 7);
                    const day = i % 7;
                    const x = week * (cellSize + gap);
                    const y = day * (cellSize + gap);
                    const level =
                        val === 0
                            ? 0
                            : Math.min(Math.ceil((val / maxVal) * (colorScale.length - 1)), colorScale.length - 1);
                    return (
                        <rect
                            key={i}
                            x={x}
                            y={y}
                            width={cellSize}
                            height={cellSize}
                            rx={2}
                            fill={colorScale[level]}
                            className="transition-all duration-200 hover:brightness-125"
                            style={{ animation: `oe-heat-pop 0.4s ease-out ${i * 4}ms both` }}
                        >
                            <title>{val} events</title>
                        </rect>
                    );
                })}
            </svg>
        </div>
    );
}

/* ── 16. SeverityMeter ────────────────────────────────────────────────── */

export function SeverityMeter({
    segments,
    height = 8,
    className = "",
}: {
    segments: { label: string; value: number; color: string }[];
    height?: number;
    className?: string;
}) {
    const total = segments.reduce((s, seg) => s + seg.value, 0);

    return (
        <div className={`${className}`}>
            <div className="flex overflow-hidden rounded-full" style={{ height }}>
                {segments.map((seg, i) => {
                    const pct = total > 0 ? (seg.value / total) * 100 : 0;
                    return (
                        <div
                            key={i}
                            className="transition-all duration-700 ease-out first:rounded-l-full last:rounded-r-full"
                            style={{
                                width: `${pct}%`,
                                backgroundColor: seg.color,
                                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.2)`,
                            }}
                            title={`${seg.label}: ${seg.value}`}
                        />
                    );
                })}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {segments.map((seg, i) => (
                    <div key={i} className="flex items-center gap-1 text-[10px] text-zinc-500 dark:text-zinc-400">
                        <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: seg.color }} />
                        <span className="font-medium">{seg.label}</span>
                        <span className="font-mono">{seg.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── 17. LiveIndicator ────────────────────────────────────────────────── */

export function LiveIndicator({ label = "LIVE", className = "" }: { label?: string; className?: string }) {
    useInjectKeyframes();
    return (
        <div
            className={`inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 dark:bg-red-500/15 ${className}`}
        >
            <span
                className="h-2 w-2 rounded-full bg-red-500 shadow-lg shadow-red-500/50"
                style={{ animation: "oe-blink-live 1.2s ease-in-out infinite" }}
            />
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-red-600 dark:text-red-400">
                {label}
            </span>
        </div>
    );
}

/* ── 18. SkeletonPulse ────────────────────────────────────────────────── */

export function SkeletonPulse({
    width,
    height = 16,
    rounded = "rounded-md",
    className = "",
}: {
    width?: number | string;
    height?: number | string;
    rounded?: string;
    className?: string;
}) {
    useInjectKeyframes();
    return (
        <div
            className={`${rounded} ${className}`}
            style={{
                width: width ?? "100%",
                height,
                background:
                    "linear-gradient(90deg, rgba(120,120,120,0.08) 25%, rgba(120,120,120,0.18) 50%, rgba(120,120,120,0.08) 75%)",
                backgroundSize: "200% 100%",
                animation: "oe-shimmer 1.8s linear infinite",
            }}
        />
    );
}

/* ── 19. GlowCard ─────────────────────────────────────────────────────── */

export function GlowCard({
    children,
    className = "",
    glowColors = ["#3b82f6", "#8b5cf6", "#ec4899", "#3b82f6"],
}: {
    children: React.ReactNode;
    className?: string;
    glowColors?: string[];
}) {
    useInjectKeyframes();
    const gradient = `linear-gradient(90deg, ${glowColors.join(", ")})`;

    return (
        <div className={`relative ${className}`}>
            {/* Glow border */}
            <div
                className="absolute -inset-[1px] rounded-2xl opacity-50 blur-[2px] transition-opacity duration-500 hover:opacity-80"
                style={{
                    background: gradient,
                    backgroundSize: "300% 300%",
                    animation: "oe-glow-border 4s ease infinite",
                }}
            />
            {/* Inner card */}
            <div className="relative overflow-hidden rounded-2xl border border-zinc-200/20 bg-white/80 p-6 shadow-xl backdrop-blur-2xl dark:border-zinc-700/20 dark:bg-zinc-900/80">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/[0.03] via-transparent to-purple-500/[0.03]" />
                <div className="relative">{children}</div>
            </div>
        </div>
    );
}

/* ── 20. FloatingParticles ────────────────────────────────────────────── */

interface Particle {
    id: number;
    x: number;
    y: number;
    size: number;
    dx: string;
    dy: string;
    duration: number;
    delay: number;
    opacity: number;
}

export function FloatingParticles({
    count = 20,
    colors = ["#3b82f6", "#8b5cf6", "#06b6d4", "#22c55e"],
    maxSize = 4,
    className = "",
}: {
    count?: number;
    colors?: string[];
    maxSize?: number;
    className?: string;
}) {
    useInjectKeyframes();
    const particles = useMemo<Particle[]>(() => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: 1 + Math.random() * (maxSize - 1),
            dx: `${(Math.random() - 0.5) * 60}px`,
            dy: `${(Math.random() - 0.5) * 60}px`,
            duration: 8 + Math.random() * 12,
            delay: Math.random() * 8,
            opacity: 0.15 + Math.random() * 0.35,
        }));
    }, [count, maxSize]);

    return (
        <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
            {particles.map((p) => (
                <div
                    key={p.id}
                    className="absolute rounded-full"
                    style={
                        {
                            left: `${p.x}%`,
                            top: `${p.y}%`,
                            width: p.size,
                            height: p.size,
                            backgroundColor: colors[p.id % colors.length],
                            opacity: p.opacity,
                            "--dx": p.dx,
                            "--dy": p.dy,
                            animation: `oe-particle-drift ${p.duration}s ease-in-out ${p.delay}s infinite`,
                            filter: `blur(${p.size > 2.5 ? 1 : 0}px)`,
                        } as React.CSSProperties
                    }
                />
            ))}
        </div>
    );
}
